import React from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ReportDownloadPage({ reportInfo, uploadedFiles, selections, onStartOver }) {
  const handleDownload = () => {
    if (reportInfo && reportInfo.download_url) {
      window.open(`${API_URL}${reportInfo.download_url}`, '_blank');
    }
  };

  const getAnalysisSummary = () => {
    const items = [];

    // Map selections to descriptions
    const descriptions = {
      'Investment/Financial Analysis': 'Investment & Financial Analysis',
      'Employer/Business Partner Research': 'Employer/Partner Research',
      'Competitive Intelligence': 'Competitive Intelligence',
      'Regulatory & Legal Risks': 'Regulatory & Legal Risk Assessment',
      'Supply Chain & Operational Risks': 'Supply Chain & Operations Analysis',
      'Growth Opportunities & Expansion': 'Growth Opportunities Analysis',
      'Warning Signs & Red Flags': 'Warning Signs & Red Flags',
      'Sustainability & Long-Term Strategy': 'Sustainability & Long-Term Strategy',
      'Customer & Revenue Concentration': 'Customer & Revenue Concentration'
    };

    if (selections.round1) items.push(descriptions[selections.round1]);
    if (selections.round2) items.push(descriptions[selections.round2]);
    if (selections.round3) items.push(descriptions[selections.round3]);

    return items;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const totalSize = uploadedFiles.reduce((sum, file) => sum + (file.size || 0), 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-border p-8">
      <div className="text-center mb-8">
        {/* Success Icon */}
        <div className="inline-flex items-center justify-center w-20 h-20 bg-success rounded-full mb-4">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h2 className="text-3xl font-bold text-navy mb-2">
          Your Report is Ready!
        </h2>
        <p className="text-text-secondary">
          Step 4 of 4: Download Your Analysis
        </p>
      </div>

      {/* Report Info */}
      {reportInfo && (
        <div className="bg-blue-50 border border-primary rounded-lg p-6 mb-6">
          <div className="flex items-start space-x-4">
            <div className="text-5xl">📄</div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-navy mb-2">
                {reportInfo.filename}
              </h3>
              <div className="space-y-1 text-sm text-text-secondary">
                <p>
                  <span className="font-medium">Company:</span> {reportInfo.company_name} ({reportInfo.ticker})
                </p>
                <p>
                  <span className="font-medium">Pages:</span> 6 pages (1 cover page + 5 content pages)
                </p>
                <p>
                  <span className="font-medium">Format:</span> Professional PDF Report
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Analyzed */}
      <div className="mb-6">
        <h4 className="font-semibold text-navy mb-3">Documents Analyzed:</h4>
        <div className="space-y-2">
          {uploadedFiles.filter(f => f.status === 'uploaded').map((file) => (
            <div key={file.id} className="flex items-center space-x-3 text-sm">
              <span className="text-success">✓</span>
              <span className="text-navy">{file.name}</span>
              <span className="text-text-secondary">({formatFileSize(file.size)})</span>
            </div>
          ))}
        </div>
        <p className="text-sm text-text-secondary mt-2">
          Total: {uploadedFiles.filter(f => f.status === 'uploaded').length} file(s), {formatFileSize(totalSize)}
        </p>
      </div>

      {/* Analysis Content */}
      <div className="mb-6">
        <h4 className="font-semibold text-navy mb-3">This report contains:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-start space-x-2">
            <span className="text-primary mt-0.5">●</span>
            <span className="text-sm text-text-secondary">Company Overview & Leadership</span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-primary mt-0.5">●</span>
            <span className="text-sm text-text-secondary">Financial Performance Analysis</span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-primary mt-0.5">●</span>
            <span className="text-sm text-text-secondary">Risk Assessment & Legal Proceedings</span>
          </div>
          <div className="flex items-start space-x-2">
            <span className="text-primary mt-0.5">●</span>
            <span className="text-sm text-text-secondary">Strategic Initiatives & Governance</span>
          </div>
          {getAnalysisSummary().map((item, idx) => (
            <div key={idx} className="flex items-start space-x-2">
              <span className="text-primary mt-0.5">●</span>
              <span className="text-sm text-text-secondary">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Report Structure Info */}
      <div className="bg-gray-50 border border-border rounded-lg p-4 mb-8">
        <h4 className="font-semibold text-navy mb-2 text-sm">Report Structure:</h4>
        <div className="text-xs text-text-secondary space-y-1">
          <p>• <strong>Page 1:</strong> Professional cover page with company and contact information</p>
          <p>• <strong>Page 2:</strong> Company identification, leadership, and business overview</p>
          <p>• <strong>Page 3:</strong> Products, business segments, operations, and workforce</p>
          <p>• <strong>Page 4:</strong> Financial performance, balance sheet, and key ratios</p>
          <p>• <strong>Page 5:</strong> Risk factors, legal proceedings, strategy, and governance</p>
          <p>• <strong>Page 6:</strong> Your customized deep-dive analysis based on selections</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-4">
        <button
          onClick={handleDownload}
          className="w-full py-4 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center space-x-3 text-lg"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>Download Report</span>
        </button>

        <button
          onClick={onStartOver}
          className="w-full py-3 border border-border text-navy font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          Analyze More Documents
        </button>
      </div>

      {/* Info Note */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-navy">
          <strong>Note:</strong> This report uses a fixed template structure for consistency.
          All companies are analyzed using identical sections and field names, making
          reports directly comparable side-by-side.
        </p>
      </div>
    </div>
  );
}
